import { Link, useNavigate } from 'react-router-dom';
import { EditorialImage } from './ui/EditorialImage';
import { useWishlist } from '../context/WishlistContext';
import { useAddCartItem } from '../hooks/useCart';
import { useCurrency } from '../hooks/useCurrency';
import type { CatalogProduct } from '../lib/catalogTypes';

export interface ProductCardDisplayProps {
  imageAspect?: string;
  showWishlist?: boolean;
  showQuickView?: boolean;
  showPrice?: boolean;
  showBadge?: boolean;
  showAddToCart?: boolean;
  showDiscountBadge?: boolean;
}

export function ProductCard({
  product,
  onQuickView,
  display,
}: {
  product: CatalogProduct;
  onQuickView?: (product: CatalogProduct) => void;
  display?: ProductCardDisplayProps;
}) {
  const wishlist = useWishlist();
  const addCartItem = useAddCartItem();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const needsConfig = !!product.requires_configuration;

  const isOnSale = product.is_on_sale && product.discounted_price != null && product.original_price != null;
  const effectiveBase = product.starting_price ?? Number(product.base_price);
  const displayPrice = isOnSale ? product.discounted_price! : effectiveBase;
  const originalPrice = isOnSale ? product.original_price! : null;

  const showWishlist = display?.showWishlist ?? true;
  const showQuickView = (display?.showQuickView ?? true) && !!onQuickView;
  const showPrice = display?.showPrice ?? true;
  const showBadge = display?.showBadge ?? true;
  const showAddToCart = display?.showAddToCart ?? true;
  const showDiscountBadge = display?.showDiscountBadge ?? true;
  const imageAspect = display?.imageAspect ?? 'aspect-[4/5]';

  return (
    <div className="group relative">
      <div className="relative overflow-hidden bg-stone-light/40">
        <Link to={`/products/${product.slug}`}>
          <EditorialImage
            src={product.images?.[0]?.path}
            seed={`product-${product.slug}`}
            alt={product.name}
            duotone={false}
            aspect={imageAspect}
            size="card"
            className="transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </Link>

        {/* Promotion badge */}
        {showBadge && product.has_active_promotion && product.promotion_badge && (
          <div className="absolute left-0 top-3 z-10 bg-clay px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-cream">
            {product.promotion_badge}
          </div>
        )}

        {showWishlist && (
          <button
            type="button"
            onClick={() => wishlist.toggle(product.id)}
            aria-label="Toggle wishlist"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center bg-cream/90 text-ink transition-transform hover:scale-110"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill={wishlist.has(product.id) ? '#a9552e' : 'none'}
              stroke={wishlist.has(product.id) ? '#a9552e' : 'currentColor'}
              strokeWidth="1.75"
            >
              <path d="M12 21s-7.5-4.6-10-9.2C.5 8.2 2.3 4 6.3 4c2 0 3.6 1 5.7 3.4C14.1 5 15.7 4 17.7 4c4 0 5.8 4.2 4.3 7.8C19.5 16.4 12 21 12 21z" />
            </svg>
          </button>
        )}

        {showQuickView && (
          <button
            type="button"
            onClick={() => onQuickView!(product)}
            className="absolute inset-x-3 bottom-3 bg-cream/95 py-2.5 text-[11px] font-medium uppercase tracking-[0.12em] text-ink opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          >
            Quick View
          </button>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/products/${product.slug}`} className="block truncate text-sm font-medium text-ink">
            {product.name}
          </Link>
          {showPrice && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className={`text-sm font-semibold ${isOnSale ? 'text-clay' : 'text-ink'}`}>
                {format(displayPrice)}
              </span>
              {isOnSale && originalPrice != null && (
                <span className="text-xs text-stone line-through">{format(originalPrice)}</span>
              )}
              {showDiscountBadge && isOnSale && product.discount_percentage != null && (
                <span className="text-[10px] font-semibold text-clay">−{product.discount_percentage}%</span>
              )}
            </div>
          )}
        </div>

        {showAddToCart && (needsConfig ? (
          <button
            type="button"
            onClick={() => navigate(`/products/${product.slug}`)}
            aria-label="Select options"
            title="Select options"
            className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center border border-stone-light text-stone transition-colors hover:border-clay hover:text-clay"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
            </svg>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => addCartItem.mutate({ product_id: product.id, quantity: 1 })}
            disabled={addCartItem.isPending}
            aria-label="Quick add to cart"
            className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center border border-stone-light text-ink transition-colors hover:border-ink hover:bg-ink hover:text-cream disabled:opacity-50"
          >
            +
          </button>
        ))}
      </div>
    </div>
  );
}
